import { Card, CardContent, Typography } from '@mui/material';
import { Column, Row, Spacer } from '../../../components';
import {
  IntervalTypeEnum,
  PaymentTypeEnum,
  PricebookBundle,
  ProductTypeEnum,
} from '../../../models/PricebookData';
import { PaidByEnum } from '../../../models/LicenseData';
import { getAutoRenewLabel } from '../../../helpers/pricebookUtils';

interface LearnMoreSummaryCardProps {
  pricebookBundle: PricebookBundle;
  freemium?: boolean;
}

const LearnMoreSummaryCard: React.FC<LearnMoreSummaryCardProps> = ({ pricebookBundle, freemium }) => {
  const selectedPrice =
    pricebookBundle.prices.find(price => price.intervalType === IntervalTypeEnum.None) ||
    pricebookBundle.prices[0];
  const price = selectedPrice ? selectedPrice.price / 100 : 0;
  const orgPays = pricebookBundle.pricebook.paidBy === PaidByEnum.Org;
  const guidedChoice = pricebookBundle.pricebook.productType === ProductTypeEnum.GuidedChoice;

  const promotion = pricebookBundle.prices.find(price => price.introductionPeriodInDays > 1);
  const introPeriodInMonths = Math.floor((promotion?.introductionPeriodInDays ?? 0) / 30);
  const freeTrial30Day = pricebookBundle.pricebook.metadata?.freeTrial30Day === 'true';
  const meteredPayment = pricebookBundle.pricebook.paymentType === PaymentTypeEnum.Metered;
  const noCost = price === 0;
  return (
    <>
      <Card variant='ghost' color='primary'>
        <CardContent className='pad20'>
          <Row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <img
              src={pricebookBundle.pricebook.artUrl}
              alt='Product Art'
              style={{ height: '80px' }}
            />
            <Column style={{ width: '40%', margin: '10px' }}>
              {pricebookBundle.pricebook.description.map((bullet, index) => (
                <Row key={index}>
                  <Typography style={{ marginRight: '20px' }}>•</Typography>
                  <Typography variant='body1' color={guidedChoice ? 'secondary.dark' : 'secondary'}>
                    {bullet}
                  </Typography>
                </Row>
              ))}
            </Column>
            <Card style={{ alignSelf: 'center', minWidth: '100px' }} variant='innerCard'>
              <CardContent className='centered-inner'>
                <Column style={{ alignItems: 'center' }}>
                  <Row style={{ alignItems: 'center', justifyContent: 'center' }}>
                    {noCost ? null : freeTrial30Day ? (
                      <Typography
                        variant={guidedChoice ? 'p20SemiBold' : 'p20SemiBold'}
                        color='secondary.dark'>
                        30 days free
                      </Typography>
                    ) : (
                      <>
                        <Typography
                          variant={guidedChoice ? 'p20SemiBold' : 'p20SemiBold'}
                          color={guidedChoice ? 'info.main' : 'secondary'}>
                          $
                        </Typography>
                        <Typography
                          variant={guidedChoice ? 'p20SemiBold' : 'p20SemiBold'}
                          color='secondary.dark'>
                          {price}
                        </Typography>
                      </>
                    )}
                    {noCost ? (
                      <Typography variant='p12' color='secondary'>
                        No cost
                      </Typography>
                    ) : (
                      selectedPrice?.intervalType !== IntervalTypeEnum.None &&
                      !freeTrial30Day && (
                        <Typography
                          variant={guidedChoice ? 'body1' : 'h3'}
                          color={guidedChoice ? 'info.main' : 'secondary'}>
                          / {selectedPrice?.intervalType}
                        </Typography>
                      )
                    )}
                  </Row>
                  <Typography variant='p12SemiBold' color='secondary.main'>
                    {freemium
                      ? ''
                      : freeTrial30Day
                        ? 'then $14.99/month'
                        : (price === 0 || orgPays) &&
                            selectedPrice?.intervalType !== IntervalTypeEnum.Month &&
                            selectedPrice?.intervalType !== IntervalTypeEnum.Year
                          ? 'free'
                          : getAutoRenewLabel(selectedPrice)}
                    {!freemium && meteredPayment && `*`}
                  </Typography>
                </Column>
              </CardContent>
            </Card>
          </Row>
        </CardContent>
      </Card>

      {promotion ? (
        <>
          <Spacer height='xxs' />
          <Typography variant='p14' color='info.main'>
            * After {introPeriodInMonths === 1 ? '30 days' : introPeriodInMonths}
            {introPeriodInMonths > 1 && ' months'}, Advice subscription auto-renews at $14.99/month
          </Typography>
        </>
      ) : meteredPayment ? (
        freemium ? (
          <Spacer height='xs' />
        ) : (
          <Typography color='info.main' variant='body2'>
            * An invoice will be sent at the end of each month for any new or renewing licenses.
          </Typography>
        )
      ) : (
        <Spacer height='xs' />
      )}
    </>
  );
};

export default LearnMoreSummaryCard;
